import { apiClient } from '../client';
import type { ApiResponse, PaginatedResponse } from '../types';

// Detail DTO — mirrors ResponseDetailRoutesDTO in Backend/BLL/DTOs/Routes/RouteDTOs.cs:83-97.
// Most fields are nullable on the wire; the list-row variant is less nullable.
// tripIdTrimpMaps: "Trimp" typo is intentional — preserved verbatim from the .NET source.
// tripStatus and creationDate are list-only and do NOT appear here.
export interface DetailRoute {
  routeId: number;
  description: string | null;
  driverId: number | null;
  driver: string | null;
  latitudeFrom: number | null;
  longitudeFrom: number | null;
  latitudeTo: number | null;
  longitudeTo: number | null;
  customerId: number | null;
  customer: string | null;
  assetId: string | null;
  tripIdTrimpMaps: string | null;

  /**
   * Stop addresses as typed when the route was planned. Null on routes created
   * before address-first planning, which is why every consumer needs a
   * coordinate fallback rather than treating these as required.
   */
  addressFrom: string | null;
  cityFrom: string | null;
  stateFrom: string | null;
  zipFrom: string | null;
  addressTo: string | null;
  cityTo: string | null;
  stateTo: string | null;
  zipTo: string | null;

  /** The tractor and trailer this route was planned for. */
  unitId: number | null;
  trailerId: number | null;

  /**
   * True when the chosen combination is larger than the standard 53' rig
   * PC*MILER actually planned the route for.
   *
   * This is not a nicety. Trimble accepts the vehicle's height, width, weight
   * and axle count, stores them, echoes them back — and plans the identical
   * route regardless. That was verified against the live service: a passenger
   * car and an impossible 50-foot-tall, 200,000 lb combination return the same
   * distance, drive time and geometry over the same stops, while routeType and
   * hazmat both move it. So a planned route is truck-legal for a standard STAA
   * combination and no more, and the dispatcher looking at this screen is the
   * person best placed to catch that before a driver sets off.
   */
  exceedsStandardProfile: boolean;

  /** Plain-English specifics behind {@link exceedsStandardProfile}. */
  oversizeReasons: string[] | null;

  /**
   * Whether truck restrictions were applied when the route was planned.
   *
   * False means the vehicle had no recorded height, so the planner had nothing
   * to restrict on. Measured against the live service over a corridor with a
   * truck ban and low arches: a truck with no height gets byte-identically the
   * same path as a truck with no dimensions at all.
   *
   * A more serious statement than {@link exceedsStandardProfile}. That one says
   * the rig is bigger than the plan assumed; this says the plan never
   * considered restrictions at all.
   */
  restrictionsApplied?: boolean;

  /** Which dimensions were absent, so the page can name them. */
  missingDimensions?: string[] | null;

/**
 * The rig the route was planned for, and the arithmetic between the two
 * vehicles. Null when neither a unit nor a trailer is recorded on the route.
 */
  vehicle: RouteVehicle | null;

  /** Trimble trip status — "Planned", "PLANNED", …; compare case-insensitively. */
  tripStatus: string | null;
}

export async function getRouteDetail(routeId: number): Promise<DetailRoute> {
  const response = await apiClient.get<ApiResponse<DetailRoute>>(
    '/api/v1/Routes/Get',
    { params: { routeId } },
  );
  return response.data.data;
}

export interface RouteListItem {
  routeId: number;
  description: string;
  driverId: number | null;
  /**
   * The vehicle the route was planned for. Absent from this projection until
   * now, which is why the edit dialog opened its pickers empty over a route
   * that had both — there was nothing to seed them from.
   */
  unitId: number | null;
  trailerId: number | null;
  driver: string;
  latitudeFrom: number | null;
  longitudeFrom: number | null;
  latitudeTo: number | null;
  longitudeTo: number | null;
  customerId: number | null;
  customer: string;
  tripStatus: string;
  assetId: string;
  creationDate: string;

  /** Stop addresses — used to prefill the edit dialog and to label the row. */
  addressFrom: string | null;
  cityFrom: string | null;
  stateFrom: string | null;
  zipFrom: string | null;
  addressTo: string | null;
  cityTo: string | null;
  stateTo: string | null;
  zipTo: string | null;
}

export interface GetRoutesListParams {
  tripStatus?: string;
  /** 0-indexed page number (backend convention). */
  page?: number;
  pageSize?: number;
  /** Column name the backend should sort by. Slice-391 backend accepts routeId, description, tripStatus, creationDate. */
  orderBy?: string;
  /** Sort direction forwarded verbatim to the backend. */
  orderDirection?: 'asc' | 'desc';
}

/**
 * GET /api/v1/Routes/GetList
 * Server-side paginated as of slice 172.
 * Returns `PaginatedResponse<RouteListItem>` wrapped in the standard Response<T>
 * envelope. Pass `page` (0-indexed) and `pageSize` to control the page window.
 */
export async function getRoutesList(
  params?: GetRoutesListParams,
): Promise<PaginatedResponse<RouteListItem>> {
  const response = await apiClient.get<ApiResponse<PaginatedResponse<RouteListItem>>>(
    '/api/v1/Routes/GetList',
    params !== undefined ? { params } : undefined,
  );
  return response.data.data;
}

/**
 * GET /api/v1/Routes/GetList/Driver — the routes assigned to the signed-in driver.
 *
 * A driver cannot call `GetList`. That action gates on the *menu permission* for
 * "routings" and a driver's JWT menu contains only `/home`, so the API answers
 * with "The User Type Unauthorized" — which the Routings page rendered verbatim,
 * above an empty state saying no routes had been created. Both were wrong: the
 * driver had seven, and the mobile app had been listing them all along through
 * this action.
 *
 * Same response shape as {@link getRoutesList}, so callers only pick the URL.
 * Strictly narrower, too: it gates on the token's `userType` and filters to that
 * driver, so a driver can only ever read routes assigned to them.
 */
export async function getDriverRoutesList(
  params?: GetRoutesListParams,
): Promise<PaginatedResponse<RouteListItem>> {
  const response = await apiClient.get<ApiResponse<PaginatedResponse<RouteListItem>>>(
    '/api/v1/Routes/GetList/Driver',
    params !== undefined ? { params } : undefined,
  );
  return response.data.data;
}

/**
 * Wire shape for POST /api/v1/Routes/Register.
 * Mirrors Backend/LILNS_ITOW_DES/BLL/DTOs/Routes/RouteDTOs.cs:RequestRegisterRouteDTO.
 *
 * driverId — selected in the UI via `getRouteDrivers`, which needs the route's
 * customerId first (drivers are scoped to their company).
 *
 * detailPCMiller — opaque Trimble PCMiler integration payload. The .NET DTO declares
 * this as `object` — likely a JSON blob carrying HOS rules, vehicle dimensions, toll
 * preferences, etc. Slice 67 always sends null.
 * // TODO(slice-67): detailPCMiller (Trimble PCMiler integration); add in dedicated Trimble form slice.
 */
export interface RegisterRoutePayload {
  description: string | null;
  driverId: number | null;
  latitudeFrom: number | null;
  longitudeFrom: number | null;
  latitudeTo: number | null;
  longitudeTo: number | null;
  customerId: number | null;

  /**
   * Street address for each stop. PC*MILER's stop `Location` accepts an address
   * object as an alternative to coordinates, so these reach Trimble as typed —
   * there is no geocoding step, and a customer never has to know a latitude.
   * The coordinate fields above stay for the map and are optional now.
   */
  addressFrom: string | null;
  cityFrom: string | null;
  stateFrom: string | null;
  zipFrom: string | null;
  addressTo: string | null;
  cityTo: string | null;
  stateTo: string | null;
  zipTo: string | null;

  /**
   * The tractor and trailer whose dimensions become the trip's routing profile.
   * The server looks them up and combines them — the browser deliberately does
   * not build Trimble's payload.
   */
  unitId: number | null;
  trailerId: number | null;

  /**
   * Escape hatch: when non-null this object is POSTed to Trimble verbatim and
   * everything above is ignored. Left null on the normal path so the server
   * composes the trip.
   */
  detailPCMiller: unknown | null;
}

/**
 * POST /api/v1/Routes/Register
 * Creates a new route and returns the new routeId.
 * Response<long> unwrapped — the `data` field is the new numeric ID.
 */
/** A driver that can be assigned to a route, for the assignment picker. */
export interface RouteDriverOption {
  driverId: number;
  /** "First Last", already joined by the server. */
  name: string;
  /** Trimble CoPilot asset id for the driver's truck, when one is set. */
  assetId: string | null;
}

/**
 * GET /api/v1/Routes/GetDrivers?customerId={id}
 *
 * Drivers belong to a company, so the customer has to be known before the list
 * means anything — which is why the route form fetches this only once a
 * customer is chosen (or, for a customer, once their own tenant id is filled in).
 *
 * The wire shape is a generic option list: `key` is the driverId and `value` is
 * a two-element array of [display name, assetId].
 */
export async function getRouteDrivers(
  customerId: number,
  signal?: AbortSignal,
): Promise<RouteDriverOption[]> {
  const response = await apiClient.get<
    ApiResponse<{ key: number; value: string[] | null }[]>
  >('/api/v1/Routes/GetDrivers', { params: { customerId }, signal });

  return (response.data.data ?? []).map((option) => ({
    driverId: option.key,
    name: option.value?.[0] ?? '',
    assetId: option.value?.[1] ?? null,
  }));
}

export async function registerRoute(
  payload: RegisterRoutePayload,
): Promise<number> {
  const response = await apiClient.post<ApiResponse<number>>(
    '/api/v1/Routes/Register',
    payload,
  );
  return response.data.data;
}

/**
 * Wire shape for PUT /api/v1/Routes/Update?routeId={id}
 * Mirrors `RequestUpdateRouteDTO` from
 * `Backend/LILNS_ITOW_DES/BLL/DTOs/Routes/RouteDTOs.cs:27-41` — bit-for-bit
 * identical to `RegisterRoutePayload` (same 8 fields).
 */
export type UpdateRoutePayload = RegisterRoutePayload;

/**
 * PUT /api/v1/Routes/Update?routeId={id}
 * Updates an existing route. Response<bool> — returns true on success.
 */
export async function updateRoute(
  routeId: number,
  payload: UpdateRoutePayload,
): Promise<boolean> {
  const response = await apiClient.put<ApiResponse<boolean>>(
    '/api/v1/Routes/Update',
    payload,
    { params: { routeId } },
  );
  return response.data.data;
}

/**
 * DELETE /api/v1/Routes/Delete?routeId={id}
 * Soft-deletes a route. Response<bool> — returns true on success.
 */
export async function deleteRoute(routeId: number): Promise<boolean> {
  const response = await apiClient.delete<ApiResponse<boolean>>(
    '/api/v1/Routes/Delete',
    { params: { routeId } },
  );
  return response.data.data;
}

/**
 * Road geometry for a saved route, plus the coordinates the pins sit on.
 *
 * Served by the API rather than called directly: PC*MILER's Route Path service
 * needs the Trimble key, which must not reach the browser. The API also resolves
 * the stop coordinates from the planned trip, which is what makes an
 * address-only route mappable — those rows have no lat/lng of their own.
 */
export interface RoutePathResponse {
  /** GeoJSON Feature with a MultiLineString geometry, or null when unavailable. */
  geometry: {
    type?: string;
    geometry?: { type?: string; coordinates?: number[][][] };
  } | null;
  origin: { lat: number; lon: number } | null;
  destination: { lat: number; lon: number } | null;
}

/** GET /api/v1/Routes/Get/RoutePath?routeId= */
export async function getRoutePath(routeId: number): Promise<RoutePathResponse> {
  const response = await apiClient.get<ApiResponse<RoutePathResponse>>(
    '/api/v1/Routes/Get/RoutePath',
    { params: { routeId } },
  );
  return response.data.data;
}

/**
 * Flatten PC*MILER's GeoJSON into the `[lat, lng]` pairs Leaflet wants.
 *
 * GeoJSON is `[lon, lat]`; Leaflet is `[lat, lng]`. Getting that backwards puts
 * a Texas route in the Indian Ocean, so the swap is done once, here.
 */
export function routePathToLatLngs(r: RoutePathResponse | null): Array<[number, number]> {
  const segments = r?.geometry?.geometry?.coordinates;
  if (!Array.isArray(segments)) return [];
  const out: Array<[number, number]> = [];
  for (const segment of segments) {
    if (!Array.isArray(segment)) continue;
    for (const pair of segment) {
      if (Array.isArray(pair) && pair.length >= 2) out.push([pair[1], pair[0]]);
    }
  }
  return out;
}

/** The truck and trailer a route was planned for, plus their combined profile. */
export interface RouteVehicle {
  unitId: number | null;
  unitLabel: string | null;
  unitHeightInches: number | null;
  unitWidthInches: number | null;
  unitLengthInches: number | null;
  unitWeight: number | null;

  trailerId: number | null;
  trailerLabel: string | null;
  trailerType: string | null;
  trailerHeightInches: number | null;
  trailerWidthInches: number | null;
  trailerLengthInches: number | null;
  trailerWeight: number | null;

  /** What the router actually sees. */
  combinedHeightInches: number | null;
  combinedWidthInches: number | null;
  combinedLengthInches: number | null;
  combinedWeight: number | null;
}
